import { action, computed, observable } from 'mobx'
import moment from 'moment-timezone'
import { OFFPEAK_ITEM_NAME, SERVICE_FEE_ITEM_NAME } from '../../../shared-lib/commonStrings'

import ModelStore from '../../../shared-lib/stores/ModelStore'
import { dateInTimezone, nullCleanObject, timeBrackets } from '../../../shared-lib/utils'
import { $Treatment, bookingItemsTotal } from './bookingHelpers'
import { $RootStore } from '../../../src/stores/RootStore'
import { autoSyncBookingWithCalendar_shared } from '../../lib/calendarSyncHelper'

export interface $Adjustment {
  _id: string
  amount: number
  note: string
  adjustmentType: 'bonus' | 'tip' | 'penalty' | 'feeAdjustment' | 'referralReward'
  payoutDate?: Date
  professionalId: string
}

export type $AvailabilityAdded = {
  day: string
  times: Array<number>
}

export type $RequestedProfessionalResponseStatus = 'rejected' | 'accepted' | 'addedAvailability'

export interface $RequestedProfessionalResponse {
  professionalId: string
  status: $RequestedProfessionalResponseStatus
  respondedAt: Date
  availabilityAdded: Array<$AvailabilityAdded>
}

export interface $Booking {
  _id: string
  user: {
    firstName?: string
    lastName?: string
    gender?: string
  }
  professionalId: string
  status: string
  date?: Date
  address?: any
  items: Array<any>
  note: string,
  totalPrice: number
  totalFee: number
  totalDuration: number
  discount: number
  promoCodeData: any
  title: string
  requestedProfessionals: Array<string>
  requestedProfessionalResponses: Array<$RequestedProfessionalResponse>
  requestedFavouriteIds: Array<string>
  rejectedByIds: Array<string>
  clientDetails: Array<$ClientDetail>
  userId: string
  postcode: any
  clientReviewId?: string
  clientReview?: any
  type: string
  professionalReviewId: string
  adminFees: Array<$AdminFee>
  adjustments: Array<$Adjustment>
  payoutDate?: Date
  clientNote: string
  clientPictures: Array<string>
  timezone: string
  crossSellPromoCode?: string
  chat: $BookingChat
  calendarId?: string
}

export interface $BookingChat {
  messageCount: {
    pro: $ChatMessages,
    consumer: $ChatMessages
  }
}

interface $ChatMessages {
  unreadMessages: number
  sent: number
}

export interface $ClientDetail {
  accountOwner: boolean
  firstName: string
  contactNumber: string
  gender: string
  note: string
}

export interface $AdminFee {
  name: string
  amount: number
}

export interface $BookingItem {
  _id: string
  treatment: $Treatment
}

export type $BookingModel = BookingModel

export default class BookingModel extends ModelStore {
  @observable accessor data: $Booking = {
    _id: '',
    status: '',
    items: [],
    note: '',
    user: {},
    totalPrice: 0,
    totalFee: 0,
    discount: 0,
    totalDuration: 0,
    promoCodeData: {},
    clientDetails: [],
    requestedProfessionals: [],
    requestedProfessionalResponses: [],
    requestedFavouriteIds: [],
    rejectedByIds: [],
    title: '',
    userId: '',
    professionalId: '',
    postcode: {},
    type: '',
    clientReview: '',
    professionalReviewId: '',
    adminFees: [],
    adjustments: [],
    clientNote: '',
    clientPictures: [],
    timezone: 'Europe/London',
    chat: {
      messageCount: {
        pro: {
          unreadMessages: 0,
          sent: 0,
        },
        consumer: {
          unreadMessages: 0,
          sent: 0,
        },
      },
    },
  }

  fetchMethodName = 'booking'

  constructor(data: Partial<$Booking>, rootStore: $RootStore) {
    super(data, rootStore)
    this.merge(data)
  }

  // Format date with time
  @computed get formattedDateWithTime(): string {
    return this.dateInTimezone.format('Do MMM, HH:mm')
  }

  // Format date without time
  @computed get formattedDateWithoutTime(): string {
    return this.dateInTimezone.format('Do MMM \'YY')
  }

  @computed get formattedDate(): string {
    return this.dateInTimezone.format('Do MMM')
  }

  // Format date + booking duration
  @computed get formattedDateTimeBrackets(): string {
    const totalDuration = bookingItemsTotal(this.data.items, 'duration')
    return timeBrackets(this.dateInTimezone, totalDuration)
  }

  @computed get formattedDateWithDayName(): string {
    return this.dateInTimezone.format('ddd DD MMM YYYY')
  }

  @computed get dateInTimezone() {
    return dateInTimezone(this.data.date, this.data.timezone)
  }

  @computed get formattedDateForList(): string {
    return this.dateInTimezone.format('Do MMMM YYYY')
  }

  @computed get hoursBeforeBooking() {
    return moment.duration(moment(this.data.date).diff(moment())).asHours()
  }

  // Check if client is callable
  @computed get canCallClient(): boolean {
    const isBookingDay = this.dateInTimezone.isSame(new Date(), 'day')
    return this.hoursBeforeBooking < 2 && isBookingDay && (this.data.status === 'IN_TRANSIT' || this.data.status === 'ARRIVED')
  }

  @computed get totalFeeWithAdminFees(): number {
    const { totalFee, adminFees } = this.data
    const totalAdminFees = adminFees.reduce((total: number, f: $AdminFee) => total + f.amount, 0)
    return totalFee + totalAdminFees
  }

  @computed get isEliteBooking(): boolean {
    const { items } = this.data
    const eliteLineItem = items.find(item => item.treatment.name === 'Elite Professional Fee')
    return !!eliteLineItem
  }

  @computed get filteredItems() {
    const { items } = this.data

    // Filter out service fee and off peak discounts so pro does not see them
    const itemsToFilter = [SERVICE_FEE_ITEM_NAME, OFFPEAK_ITEM_NAME]
    return items.filter(i => !itemsToFilter.includes(i.treatment.name))
  }

  // Format address
  userAddressAsString(): string {
    const { address } = this.data

    if (!address) return ''

    const { firstLine, secondLine, county, postalCode } = address
    const addr = [firstLine, secondLine, county, postalCode].filter(Boolean).join(', ')
    return addr
  }

  // Reject booking
  @action.bound async rejectBooking_api() {
    const { communication: { requester } } = this.rootStore

    if (this.loading) return

    this.loading = true
    const { _id, status } = this.data
    const response = await requester.rejectBooking({ _id })

    // Add pro id to booking rejectedIds
    if (response.rejectBooking) {
      const { bookingStore } = this.rootStore
      bookingStore.items = bookingStore.items.filter((b: $BookingModel) => b.data._id !== _id)
    }

    this.loading = false

    if (status === 'REQUESTED') this.professionalRequestResponse_api('rejected')

    return true
  }

  @action.bound async hasBookingClash_api() {
    const { communication: { requester } } = this.rootStore
    const { _id } = this.data

    this.loading = true

    const hasBookingClash = await requester.hasBookingClash({ _id })

    this.loading = false
    return !!hasBookingClash.hasBookingClash
  }

  // Accept booking
  @action.bound async acceptBooking_api() {
    const { _id, type, status } = this.data
    const { communication: { requester } } = this.rootStore

    if (this.loading) return

    this.loading = true

    // Allow assigned bookings to be accepted even if there is a conflicted booking
    const ignoreConflictingBookings = type === 'assigned'

    // Accept booking
    const response = await requester.acceptBooking({ _id, ignoreConflictingBookings })

    // Add pro id to professional of booking
    if (response.acceptBooking) {
      const { userStore: { data: { _id: proId } } } = this.rootStore
      this.data.professionalId = proId

      if (status === 'REQUESTED') this.professionalRequestResponse_api('accepted')
      this.data.status = 'ACCEPTED'

      await autoSyncBookingWithCalendar_shared({ booking: this.data, store: this.rootStore })
      this.loading = false
      return true
    }

    this.loading = false
    return false
  }

  // Complete booking
  @action.bound async completeBooking_api() {
    if (this.loading) return
    const { communication: { requester } } = this.rootStore

    this.loading = true
    const { _id } = this.data
    const response = await requester.updateBookingStatus({ _id, status: 'COMPLETED' })

    // Update status of booking
    if (response.updateBookingStatus) this.data.status = 'COMPLETED'

    this.loading = false
    return true
  }

  @action.bound async professionalRequestResponse_api(status: $RequestedProfessionalResponseStatus, availabilityAdded: Array<$AvailabilityAdded> = []) {
    if (this.loading) return
    const { communication: { requester } } = this.rootStore

    this.loading = true

    const args = {
      _id: this.data._id,
      status,
      availabilityAdded,
    }

    const response = await requester.professionalRequestResponse(args)

    const professionalResponses = nullCleanObject(response.professionalRequestResponse)

    if (professionalResponses.requestedProfessionalResponses) {
      this.data.requestedProfessionalResponses = professionalResponses.requestedProfessionalResponses
    }

    this.loading = false
    return true
  }

  // Update booking status
  @action.bound async updateBookingStatus_api(status: string) {
    if (this.loading) return
    const { communication: { requester } } = this.rootStore

    this.loading = true
    const { _id } = this.data
    const response = await requester.updateBookingStatus({ _id, status })

    // Update status of booking
    if (response.updateBookingStatus) this.data.status = status

    this.loading = false
    return true
  }

  // Submit review to API
  async createReview_api(data: any): Promise<boolean> {
    this.loading = true
    const { communication: { requester } } = this.rootStore
    try {
      const ret = await requester.createReview({ data })
      const reviewData = ret.createReview

      if (reviewData) {
        // Add review _id to booking professionalReviewId
        this.set({ professionalReviewId: reviewData })
      }

      this.loading = false
      return true
    } catch (e) {
      this.loading = false
      return false
    }
  }
}
